import * as Types from '@aeppic/types'

import { unpatch } from './diff.js'

export function getPreviousChanges(document: Types.Document): PreviousChange | null {
  if (!document?.previous) {
    return null
  }

  return new PreviousChange(document)
}

export class PreviousChange {
  private _previousDataInitialized = false
  private _previousData: any
  private _previousDocumentInitialized = false
  private _previousDocument: Types.Document

  constructor(private _document: Types.Document) {}

  get info(): Types.PreviousDocumentInfo {
    return this._document.previous
  }

  version(): PreviousValueChange<string> {
    return new PreviousValueChange(
      this.info.v,
      this._document.v,
      this.info.v !== this._document.v
    )
  }

  wasChanged(): boolean {
    return !!this.info
  }

  wasMetadataChanged(): boolean {
    return (
      this.wasFormReferenceChanged() ||
      this.wasParentChanged() ||
      this.wasAncestryChanged() ||
      this.wasAncestorFormsChanged() ||
      this.wasLocksChanged() ||
      this.wasLanguageChanged() ||
      this.wasHiddenChanged() ||
      this.wasAttachedChanged() ||
      this.wasReadonlyChanged() ||
      this.wasFrozenChanged()
    )
  }

  previousDocument(): Types.Document {
    return cloneValue(this._getPreviousDocument())
  }

  currentDocument(): Types.Document {
    return cloneValue(this._document)
  }

  data(): PreviousValueChange<any> {
    return new PreviousValueChange(
      this._getPreviousData(),
      this._document.data,
      this._wasDataChanged()
    )
  }

  previous(path = ''): any {
    return cloneValue(readPath(this._getPreviousDocument(), path))
  }

  current(path = ''): any {
    return cloneValue(readPath(this._document, path))
  }

  field(fieldName: string): PreviousFieldChange {
    const normalizedFieldName = normalizeDataPath(fieldName)
    return new PreviousFieldChange(this, normalizedFieldName)
  }

  parent(): PreviousValueChange<string> {
    return new PreviousValueChange(
      this._previousOrCurrent('p'),
      this._document.p,
      this.wasParentChanged()
    )
  }

  form(): PreviousFormChange {
    return new PreviousFormChange(
      this._previousOrCurrent('f'),
      this._document.f,
      this.wasFormReferenceChanged()
    )
  }

  ancestry(): PreviousListChange<string> {
    return new PreviousListChange(
      this._previousOrCurrent('a') ?? [],
      this._document.a ?? [],
      this.wasAncestryChanged()
    )
  }

  ancestorForms(): PreviousListChange<string> {
    return new PreviousListChange(
      this._previousOrCurrent('a_forms') ?? [],
      this._document.a_forms ?? [],
      this.wasAncestorFormsChanged()
    )
  }

  locks(): PreviousListChange<Types.Reference> {
    return new PreviousListChange(
      this._previousOrCurrent('locks') ?? [],
      this._document.locks ?? [],
      this.wasLocksChanged()
    )
  }

  language(): PreviousValueChange<string> {
    return new PreviousValueChange(
      this._previousOrCurrent('language'),
      this._document.language,
      this.wasLanguageChanged()
    )
  }

  hidden(): PreviousValueChange<boolean> {
    return new PreviousValueChange(
      this._previousOrCurrent('hidden') ?? false,
      this._document.hidden ?? false,
      this.wasHiddenChanged()
    )
  }

  attached(): PreviousValueChange<boolean> {
    return new PreviousValueChange(
      this._previousOrCurrent('attached') ?? false,
      this._document.attached ?? false,
      this.wasAttachedChanged()
    )
  }

  readonly(): PreviousValueChange<boolean> {
    return new PreviousValueChange(
      this._previousOrCurrent('readonly') ?? false,
      this._document.readonly ?? false,
      this.wasReadonlyChanged()
    )
  }

  frozen(): PreviousValueChange<Types.ChangeSourceInfo | null> {
    return new PreviousValueChange(
      this._previousOrCurrent('frozen') ?? null,
      this._document.frozen ?? null,
      this.wasFrozenChanged()
    )
  }

  wasFormReferenceChanged(): boolean {
    return hasOwn(this.info, 'f')
  }

  wasFormUpgraded(): boolean {
    return this.form().wasUpgraded()
  }

  wasFormChanged(): boolean {
    return this.form().wasChangedToDifferentForm()
  }

  wasParentChanged(): boolean {
    return hasOwn(this.info, 'p')
  }

  wasMoved(): boolean {
    return this.wasParentChanged() || this.wasAncestryChanged() || this.wasAncestorFormsChanged()
  }

  wasMovedFrom(parentId: string): boolean {
    return this.parent().wasChangedFrom(parentId)
  }

  wasMovedTo(parentId: string): boolean {
    return this.parent().wasChangedTo(parentId)
  }

  wasAncestryChanged(): boolean {
    return hasOwn(this.info, 'a')
  }

  wasAncestorFormsChanged(): boolean {
    return hasOwn(this.info, 'a_forms')
  }

  wasLocksChanged(): boolean {
    return hasOwn(this.info, 'locks')
  }

  wasLanguageChanged(): boolean {
    return hasOwn(this.info, 'language')
  }

  wasHiddenChanged(): boolean {
    return hasOwn(this.info, 'hidden')
  }

  wasAttachedChanged(): boolean {
    return hasOwn(this.info, 'attached')
  }

  wasReadonlyChanged(): boolean {
    return hasOwn(this.info, 'readonly')
  }

  wasFrozenChanged(): boolean {
    return hasOwn(this.info, 'frozen')
  }

  _fieldBefore(fieldName: string): any {
    return readPath(this._getPreviousData(), fieldName)
  }

  _fieldAfter(fieldName: string): any {
    return readPath(this._document.data, fieldName)
  }

  _wasFieldChanged(fieldName: string): boolean {
    return !deepEqual(this._fieldBefore(fieldName), this._fieldAfter(fieldName))
  }

  private _wasDataChanged(): boolean {
    return !!this.info.patch
  }

  private _previousOrCurrent(key: string): any {
    if (hasOwn(this.info, key)) {
      return this.info[key]
    }

    return this._document[key]
  }

  private _getPreviousData(): any {
    if (!this._previousDataInitialized) {
      const currentData = cloneValue(this._document.data)

      this._previousData = this._wasDataChanged()
        ? unpatch(currentData, this.info.patch)
        : currentData
      this._previousDataInitialized = true
    }

    return this._previousData
  }

  private _getPreviousDocument(): Types.Document {
    if (!this._previousDocumentInitialized) {
      const previousDocument = cloneValue(this._document)

      previousDocument.v = this.info.v
      previousDocument.data = cloneValue(this._getPreviousData())
      delete previousDocument.previous

      if (this.wasFormReferenceChanged()) {
        previousDocument.f = cloneValue(this.info.f)
      }

      if (this.wasParentChanged()) {
        previousDocument.p = this.info.p
      }

      if (this.wasAncestryChanged()) {
        previousDocument.a = cloneValue(this.info.a)
      }

      if (this.wasAncestorFormsChanged()) {
        previousDocument.a_forms = cloneValue(this.info.a_forms)
      }

      if (this.wasLocksChanged()) {
        previousDocument.locks = cloneValue(this.info.locks)
      }

      if (this.wasLanguageChanged()) {
        previousDocument.language = this.info.language
      }

      if (this.wasHiddenChanged()) {
        previousDocument.hidden = this.info.hidden
      }

      if (this.wasAttachedChanged()) {
        previousDocument.attached = this.info.attached
      }

      if (this.wasReadonlyChanged()) {
        previousDocument.readonly = this.info.readonly
      }

      if (this.wasFrozenChanged()) {
        if (this.info.frozen) {
          previousDocument.frozen = cloneValue(this.info.frozen)
        } else {
          delete previousDocument.frozen
        }
      }

      this._previousDocument = previousDocument
      this._previousDocumentInitialized = true
    }

    return this._previousDocument
  }
}

export class PreviousValueChange<T = any> {
  constructor(
    private _before: T,
    private _after: T,
    private _changed: boolean
  ) {}

  before(): T {
    return cloneValue(this._before)
  }

  after(): T {
    return cloneValue(this._after)
  }

  wasChanged(): boolean {
    return this._changed
  }

  wasChangedFrom(value: any): boolean {
    return this.wasChanged() && matchesValue(this._before, value)
  }

  wasChangedTo(value: any): boolean {
    return this.wasChanged() && matchesValue(this._after, value)
  }

  wasChangedFromTo(from: any, to: any): boolean {
    return this.wasChangedFrom(from) && this.wasChangedTo(to)
  }

  wasSet(): boolean {
    return this.wasChanged() && isEmptyValue(this._before) && !isEmptyValue(this._after)
  }

  wasCleared(): boolean {
    return this.wasChanged() && !isEmptyValue(this._before) && isEmptyValue(this._after)
  }
}

export class PreviousFieldChange extends PreviousValueChange {
  constructor(private _previous: PreviousChange, private _fieldName: string) {
    super(
      _previous._fieldBefore(_fieldName),
      _previous._fieldAfter(_fieldName),
      _previous._wasFieldChanged(_fieldName)
    )
  }

  name(): string {
    return this._fieldName
  }

  asList(): PreviousListChange {
    return new PreviousListChange(this.before(), this.after(), this.wasChanged())
  }
}

export class PreviousListChange<T = any> extends PreviousValueChange<T[]> {
  constructor(before: T[], after: T[], changed: boolean) {
    super(before ?? [], after ?? [], changed)
  }

  added(): T[] {
    return getAddedValues(this.before(), this.after())
  }

  wasAdded(value: any): boolean {
    return containsValue(this.added(), value)
  }

  removed(): T[] {
    return getRemovedValues(this.before(), this.after())
  }

  wasRemoved(value: any): boolean {
    return containsValue(this.removed(), value)
  }

  updated(): T[] {
    return getUpdatedValues(this.before(), this.after())
  }

  wasUpdated(value: any): boolean {
    return containsValue(this.updated(), value)
  }

  moved(): T[] {
    return getMovedValues(this.before(), this.after())
  }

  wasMoved(value: any): boolean {
    return containsValue(this.moved(), value)
  }

  appended(): T[] {
    return getAppendedValues(this.before(), this.after())
  }

  wasAppended(value: any): boolean {
    return containsValue(this.appended(), value)
  }

  prepended(): T[] {
    return getPrependedValues(this.before(), this.after())
  }

  wasPrepended(value: any): boolean {
    return containsValue(this.prepended(), value)
  }

  inserted(): T[] {
    return this.added()
  }

  wasInserted(value: any): boolean {
    return this.wasAdded(value)
  }

  wasInsertedAt(value: any, index: number): boolean {
    return containsValue(getInsertedAtValues(this.before(), this.after(), index), value)
  }
}

export class PreviousFormChange extends PreviousValueChange<Types.Reference> {
  wasUpgraded(): boolean {
    const before = this.before()
    const after = this.after()

    return this.wasChanged() && before?.id === after?.id && before?.v !== after?.v
  }

  wasChangedToDifferentForm(): boolean {
    const before = this.before()
    const after = this.after()

    return this.wasChanged() && before?.id !== after?.id
  }
}

function normalizeDataPath(fieldName: string): string {
  return fieldName.startsWith('data.') ? fieldName.slice('data.'.length) : fieldName
}

function readPath(source: any, path: string): any {
  if (!path) {
    return source
  }

  let current = source

  for (const part of path.split('.')) {
    if (current == null) {
      return undefined
    }

    current = current[part]
  }

  return current
}

function cloneValue<T>(value: T): T {
  if (value == null) {
    return value
  }

  return JSON.parse(JSON.stringify(value))
}

function hasOwn(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key)
}

function getAddedValues(before: any, after: any): any[] {
  if (Array.isArray(after)) {
    return getAddedItems(before, after).map(item => cloneValue(item.value))
  }

  if (isEmptyValue(before) && !isEmptyValue(after)) {
    return [cloneValue(after)]
  }

  return []
}

function getRemovedValues(before: any, after: any): any[] {
  if (Array.isArray(before)) {
    return getRemovedItems(before, after).map(item => cloneValue(item.value))
  }

  if (!isEmptyValue(before) && isEmptyValue(after)) {
    return [cloneValue(before)]
  }

  return []
}

function getUpdatedValues(before: any, after: any): any[] {
  if (Array.isArray(before) && Array.isArray(after)) {
    const unmatchedBefore = before.map(value => ({ value, matched: false }))
    return after
      .filter(afterValue => {
        const beforeItem = unmatchedBefore.find(item => !item.matched && sameIdentity(item.value, afterValue))

        if (!beforeItem) {
          return false
        }

        beforeItem.matched = true
        return !deepEqual(beforeItem.value, afterValue)
      })
      .map(cloneValue)
  }

  if (!Array.isArray(before) && !Array.isArray(after) && !isEmptyValue(before) && !isEmptyValue(after) && !deepEqual(before, after)) {
    return [cloneValue(after)]
  }

  return []
}

function getMovedValues(before: any, after: any): any[] {
  if (!Array.isArray(before) || !Array.isArray(after)) {
    return []
  }

  return after
    .filter((afterValue, afterIndex) => {
      const beforeIndex = before.findIndex(beforeValue => sameIdentity(beforeValue, afterValue))
      return beforeIndex !== -1 && beforeIndex !== afterIndex
    })
    .map(cloneValue)
}

function getAppendedValues(before: any, after: any): any[] {
  if (!Array.isArray(after)) {
    return []
  }

  const beforeLength = Array.isArray(before) ? before.length : 0
  return getAddedItems(before, after)
    .filter(item => item.index >= beforeLength)
    .map(item => cloneValue(item.value))
}

function getPrependedValues(before: any, after: any): any[] {
  if (!Array.isArray(after)) {
    return []
  }

  const addedItems = getAddedItems(before, after)
  return addedItems
    .filter(item => item.index < addedItems.length)
    .map(item => cloneValue(item.value))
}

function getInsertedAtValues(before: any, after: any, index: number): any[] {
  if (!Array.isArray(after)) {
    return []
  }

  return getAddedItems(before, after)
    .filter(item => item.index === index)
    .map(item => cloneValue(item.value))
}

function getAddedItems(before: any, after: any[]): { value: any, index: number }[] {
  const beforeItems = (Array.isArray(before) ? before : []).map(value => ({ value, matched: false }))
  const addedItems: { value: any, index: number }[] = []

  after.forEach((value, index) => {
    const beforeItem = beforeItems.find(item => !item.matched && sameIdentity(item.value, value))

    if (beforeItem) {
      beforeItem.matched = true
    } else {
      addedItems.push({ value, index })
    }
  })

  return addedItems
}

function getRemovedItems(before: any[], after: any): { value: any, index: number }[] {
  const afterItems = (Array.isArray(after) ? after : []).map(value => ({ value, matched: false }))
  const removedItems: { value: any, index: number }[] = []

  before.forEach((value, index) => {
    const afterItem = afterItems.find(item => !item.matched && sameIdentity(item.value, value))

    if (afterItem) {
      afterItem.matched = true
    } else {
      removedItems.push({ value, index })
    }
  })

  return removedItems
}

function containsValue(values: any[], value: any): boolean {
  return values.some(candidate => matchesValue(candidate, value))
}

function matchesValue(actual: any, expected: any): boolean {
  if (hasId(actual)) {
    if (typeof expected === 'string') {
      return actual.id === expected
    }

    if (hasId(expected)) {
      return actual.id === expected.id && (!expected.v || actual.v === expected.v)
    }
  }

  return deepEqual(actual, expected)
}

function hasId(value: any): value is { id: string, v?: string } {
  return value != null && typeof value === 'object' && typeof value.id === 'string' && value.id !== ''
}

function sameIdentity(left: any, right: any): boolean {
  if (hasId(left) && hasId(right)) {
    return left.id === right.id
  }

  return deepEqual(left, right)
}

function isEmptyValue(value: any): boolean {
  if (value == null || value === '') {
    return true
  }

  if (Array.isArray(value)) {
    return value.length === 0
  }

  if (hasId(value)) {
    return value.id === ''
  }

  return false
}

function deepEqual(left: any, right: any): boolean {
  if (left === right) {
    return true
  }

  if (typeof left !== typeof right) {
    return false
  }

  if (left == null || right == null || typeof left !== 'object') {
    return false
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false
    }

    return left.every((value, index) => deepEqual(value, right[index]))
  }

  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every(key => hasOwn(right, key) && deepEqual(left[key], right[key]))
}
